import { useQuery } from '@tanstack/react-query';
import {
  FeeCenterResponse,
  type FeeCenterAccountActivityRow,
  type FeeCenterActualRow,
  type FeeCenterProvenance,
  type FeeCenterResponse as FeeCenterResponseData,
  type FeeCenterTotalsRow,
} from '@sourcito/schemas';
import { apiFetch } from './api';

export interface FeeCenterPeriod {
  /** Complete calendar-month bounds in the API's YYYY-MM-DD format. */
  from: string;
  to: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** The API's default: the three most recently completed calendar months. */
export function defaultFeeCenterPeriod(now: Date = new Date()): FeeCenterPeriod {
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 3, 1));
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
  return { from: isoDate(from), to: isoDate(to) };
}

export function useFeeCenter(period: FeeCenterPeriod, enabled = true) {
  return useQuery({
    queryKey: ['fee-center', period.from, period.to] as const,
    enabled,
    queryFn: () =>
      apiFetch(
        `/api/fee-center?from=${encodeURIComponent(period.from)}&to=${encodeURIComponent(period.to)}`,
        { responseSchema: FeeCenterResponse }
      ),
  });
}

export function parseFeeCenterResponse(input: unknown): FeeCenterResponseData {
  return FeeCenterResponse.parse(input);
}

function currencyCode(currency: string): string | null {
  const normalized = currency.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(normalized) ? normalized : null;
}

export function formatFeeCenterCurrency(cents: number | null, currency = 'USD'): string {
  if (cents == null) return '—';
  const code = currencyCode(currency);
  if (!code) return `${(cents / 100).toFixed(2)} ${currency.trim() || 'unknown currency'}`;
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Format a signed impact with a typographically clear minus sign. */
export function formatFeeCenterDelta(cents: number | null, currency = 'USD'): string {
  if (cents == null) return '—';
  if (cents >= 0) return formatFeeCenterCurrency(cents, currency);
  return `−${formatFeeCenterCurrency(Math.abs(cents), currency)}`;
}

/**
 * R2 item 3 (D4, docs/plans/2026-09-03-graded-kernel.md) — the Fee Center
 * header's "matches Finance" / "differs from Finance by $X" line, from the
 * `totals` rows the kernel cross-check (D3) attaches. Undefined/empty
 * `totals` (an older API build, or a period with no settlement currency at
 * all) reports 'unknown' rather than a false "matches".
 */
export type FeeCenterKernelMatch =
  | { status: 'matches' }
  | { status: 'differs'; label: string }
  | { status: 'unknown' };

export function feeCenterKernelMatch(
  totals: readonly FeeCenterTotalsRow[] | undefined
): FeeCenterKernelMatch {
  if (!totals || totals.length === 0) return { status: 'unknown' };
  const mismatched = totals.filter((row) => row.kernelDeltaCents !== 0);
  if (mismatched.length === 0) return { status: 'matches' };
  const label = mismatched
    .map((row) => formatFeeCenterDelta(row.kernelDeltaCents, row.currency))
    .join(', ');
  return { status: 'differs', label };
}

export type FeeCenterActivityDirection = 'Charge' | 'Credit';

/** Human-readable polarity for the signed settlement impact. */
export function feeCenterActivityDirection(cents: number): FeeCenterActivityDirection {
  return cents < 0 ? 'Credit' : 'Charge';
}

/** Keep product/order attribution explicit instead of implying every line is SKU-linked. */
export function feeCenterActivityLinkage(
  row: Pick<FeeCenterAccountActivityRow, 'scope' | 'orderCount' | 'skuCount'>
): string {
  if (row.scope === 'account') return 'Account-level — Amazon supplied no SKU';
  const orders = `${row.orderCount} ${row.orderCount === 1 ? 'order' : 'orders'}`;
  const skus = `${row.skuCount} ${row.skuCount === 1 ? 'SKU' : 'SKUs'}`;
  const linked = `Linked to ${orders} / ${skus}`;
  return row.scope === 'mixed'
    ? `Mixed scope — ${linked.toLowerCase()}; some lines have no SKU`
    : linked;
}

/** Rank movements by absolute dollars while keeping ties stable and predictable. */
export function rankAccountActivity<
  T extends {
    netChargeCents: number;
    periodStart: string;
    currency: string;
    label: string;
    amountType?: string;
    amountDescription?: string;
  },
>(rows: readonly T[]): T[] {
  return [...rows].sort(
    (a, b) =>
      Math.abs(b.netChargeCents) - Math.abs(a.netChargeCents) ||
      a.periodStart.localeCompare(b.periodStart) ||
      a.currency.localeCompare(b.currency) ||
      a.label.localeCompare(b.label) ||
      (a.amountType ?? '').localeCompare(b.amountType ?? '') ||
      (a.amountDescription ?? '').localeCompare(b.amountDescription ?? '')
  );
}

export function summarizeAccountActivity<
  T extends { currency: string; netChargeCents: number; lineCount: number },
>(rows: readonly T[]): Array<Pick<T, 'currency' | 'netChargeCents' | 'lineCount'>> {
  const totals = new Map<string, { currency: string; netChargeCents: number; lineCount: number }>();
  for (const row of rows) {
    const total = totals.get(row.currency) ?? {
      currency: row.currency,
      netChargeCents: 0,
      lineCount: 0,
    };
    total.netChargeCents += row.netChargeCents;
    total.lineCount += row.lineCount;
    totals.set(row.currency, total);
  }
  return [...totals.values()].sort((a, b) => a.currency.localeCompare(b.currency));
}

/** Explain a product/month total without hiding whether it was a charge or credit. */
export function feeCenterActualSummary(
  row: Pick<FeeCenterActualRow, 'actualFeesCents' | 'revenueCents' | 'currency'>
): string {
  const revenue = formatFeeCenterDelta(row.revenueCents, row.currency);
  if (row.actualFeesCents > 0) {
    return `Amazon kept ${formatFeeCenterCurrency(row.actualFeesCents, row.currency)} from ${revenue} of sales.`;
  }
  if (row.actualFeesCents < 0) {
    return `Amazon credited ${formatFeeCenterCurrency(Math.abs(row.actualFeesCents), row.currency)} against ${revenue} of sales.`;
  }
  return `No net Amazon fee was recorded against ${revenue} of sales.`;
}

export function formatFeeCenterProductUnitCost(
  cents: number | null,
  unitsSold: number | null,
  currency: string
): string {
  if (unitsSold === null) return 'Sales coverage incomplete';
  if (unitsSold === 0) return 'No sold units in this period';
  return cents === null ? '—' : `${formatFeeCenterDelta(cents, currency)} / sold unit`;
}

export function feeCenterFulfillmentEvidenceCopy(): string {
  return 'Charged fulfillment is included in this product’s settlement fees; expected is a separate current-price estimate. Storage and aging use separate reports.';
}

export function feeCenterProductTitle(
  row: Pick<FeeCenterActualRow, 'productName' | 'sellerSku'>
): string {
  return row.productName?.trim() || row.sellerSku;
}

export function feeCenterProductIdentifiers(
  row: Pick<FeeCenterActualRow, 'sellerSku' | 'asin'>
): string {
  const asin = row.asin?.trim();
  return asin ? `SKU ${row.sellerSku} · ASIN ${asin}` : `SKU ${row.sellerSku}`;
}

export function formatFeeCenterPercent(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;
}

export function formatFeeCenterUnitsPerDay(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value.toLocaleString('en-US', { maximumFractionDigits: 2 })} units/day`;
}

export function formatFeeCenterVelocityUnavailable(): string {
  return 'FBA velocity unavailable — sales coverage or channel data incomplete';
}

export function formatFeeCenterSurchargeCountdown(days: number | null): string {
  if (days == null) return 'Countdown unavailable';
  if (days === 0) return 'Surcharge due today';
  if (days < 0) return `${Math.abs(days)} days past surcharge`;
  return `${days} ${days === 1 ? 'day' : 'days'} until surcharge`;
}

export function expectedFeeUnavailableCopy(
  reason: 'fresh_same_price' | 'stale_stamp' | 'price_mismatch' | 'no_stamp'
): string {
  if (reason === 'stale_stamp') return 'Expected fee unavailable — fee stamp is stale';
  if (reason === 'price_mismatch') {
    return 'Expected fee unavailable — checked price does not match current price';
  }
  if (reason === 'no_stamp') return 'Expected fee unavailable — no fee stamp';
  return 'Expected fee unavailable — fulfillment units unavailable';
}

export function canAccessFeeCenter(role: string | undefined, amazonEnabled: boolean): boolean {
  return (role === 'owner' || role === 'operator') && amazonEnabled;
}

export function formatFeeCenterDate(value: string | null): string {
  if (!value) return 'Date not supplied';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date not supplied';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function agingDateCopy(value: string | null): string {
  return value ? `Surcharge date: ${formatFeeCenterDate(value)}` : 'Date not supplied';
}

export type FeeCenterCoverageStatus = 'unknown' | 'backfilling' | 'caught_up' | 'pending';

export interface FeeCenterCoverageCopy {
  title: string;
  detail: string;
}

export function feeCenterCoverageCopy(
  status: FeeCenterCoverageStatus,
  reportName: string
): FeeCenterCoverageCopy | null {
  if (status === 'caught_up') return null;
  if (status === 'backfilling') {
    return {
      title: `${reportName} are still backfilling`,
      detail: `${reportName.replace(/ fees$/i, '')} dollars are withheld until the named report window is complete.`,
    };
  }
  if (status === 'pending') {
    const affected = reportName === 'Inventory planning' ? 'aging' : reportName.toLowerCase();
    return {
      title: `${reportName} report pending`,
      detail: `A report is pending; affected ${affected} conclusions remain unavailable.`,
    };
  }
  return {
    title: `${reportName} coverage unavailable`,
    detail: `No complete ${reportName.toLowerCase()} coverage is recorded; affected conclusions are withheld.`,
  };
}

function sourceLabel(source: string): string {
  if (source === 'GET_FBA_STORAGE_FEE_CHARGES_DATA') return 'Amazon storage report';
  if (source === 'GET_FBA_INVENTORY_PLANNING_DATA') return 'Amazon inventory planning report';
  if (source === 'amazon_settlement_lines') return 'Amazon settlement lines';
  if (source === 'amazon_fee_change_events') return 'Fee-change audit events';
  return source.replaceAll('_', ' ');
}

function periodLabel(provenance: FeeCenterProvenance): string {
  if (!provenance.periodStart || !provenance.periodEnd) return 'Period not supplied';
  return `${formatFeeCenterDate(provenance.periodStart)} – ${formatFeeCenterDate(provenance.periodEnd)}`;
}

export function feeCenterProvenanceLabel(provenance: FeeCenterProvenance): string {
  const freshness =
    provenance.freshness === 'fresh'
      ? 'Fresh'
      : provenance.freshness === 'stale'
        ? 'Stale'
        : 'Freshness unknown';
  const coverage =
    provenance.coverage === 'complete'
      ? 'Coverage complete'
      : provenance.coverage === 'backfilling'
        ? 'Backfilling'
        : provenance.coverage === 'pending'
          ? 'Pending'
          : 'Coverage unknown';
  return `${sourceLabel(provenance.source)} · ${periodLabel(provenance)} · ${freshness} · ${coverage}`;
}

export function rankFulfillmentByDelta<T extends { deltaCents: number | null }>(
  rows: readonly T[]
): T[] {
  return [...rows].sort((a, b) => {
    if (a.deltaCents == null && b.deltaCents == null) return 0;
    if (a.deltaCents == null) return 1;
    if (b.deltaCents == null) return -1;
    return Math.abs(b.deltaCents) - Math.abs(a.deltaCents);
  });
}

/** Keep this small utility exported for page copy and tests around period math. */
export function calendarDaysInPeriod(period: FeeCenterPeriod): number {
  const from = new Date(`${period.from}T00:00:00.000Z`);
  const to = new Date(`${period.to}T23:59:59.999Z`);
  return Math.round((to.getTime() - from.getTime() + 1) / DAY_MS);
}
